
import React, { type Node } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Avatar } from '../../base/avatar';
import {
    Icon,
    IconArrowDownSmall,
    IconArrowRight,
    IconCameraEmpty,
    IconCameraEmptyDisabled,
    IconDominantSpeaker,
    IconMicrophoneEmpty,
    IconMicrophoneEmptySlash,
    IconNetwork,
    IconRaisedHandHollow,
    IconUserGroup
} from '../../base/icons';
import {
    PARTICIPANT_ROLE,
    getParticipantDisplayNameWithId,
    isParticipantPanelist,
    isParticipantPresenter
} from '../../base/participants';
import { Tooltip } from '../../base/tooltip';
import ParticipantActionEllipsis from '../../breakout-rooms/components/web/ParticipantActionEllipsis';
import ConnectionIndicator from '../../connection-indicator/components/web/ConnectionIndicator';
import { areDeviceIndicatorsEnabled, getParticipantSessions } from '../../session-persistence/functions';
import {
    ACTION_TRIGGER,
    type ActionTrigger,
    MEDIA_STATE,
    type MediaState
} from '../constants';

import ParticipantPermissionsIndicator from './ParticipantPermissionsIndicator';
import RoleIndicators from './RoleIndicators';
import {
    ColoredIcon,
    ParticipantActionsHover,
    ParticipantActionsPermanent,
    ParticipantContainer,
    ParticipantContent,
    ParticipantDetailsContainer,
    ParticipantName,
    ParticipantNameContainer
} from './styled';

/**
 * Participant actions component mapping depending on trigger type.
 */
const Actions = {
    [ACTION_TRIGGER.HOVER]: ParticipantActionsHover,
    [ACTION_TRIGGER.PERMANENT]: ParticipantActionsPermanent
};

const participantIconStyle = {
    width: '28px',
    height: '28px',
    background: '#e4ebf5',
    borderRadius: '50px'
};

/**
 * Returns the icon mapping for possible participant audio states.
 *
 * @param {Object} participant - The participant object.
 * @returns {React$Element<any> | null}
 */
const getAudioStateIcon = (firstParticipantInList: Boolean, audioMediaState: MediaState): React$Element<any> | null => {
    const icons = {
        [MEDIA_STATE.FORCE_MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.fMute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmptySlash } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.mute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmptySlash } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.UNMUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.unmute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmpty } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.NONE]: null
    };

    return icons[audioMediaState];
};

/**
 * Returns the icon mapping for possible participant video states.
 *
 * @param {Object} participant - The participant object.
 * @returns {React$Element<any> | null}
 */
const getVideoStateIcon = (firstParticipantInList: Boolean, videoMuteState: MediaState): React$Element<any> | null => {
    const icons = {
        [MEDIA_STATE.FORCE_MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoFMuted' }
                position = { firstParticipantInList ? 'bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmptyDisabled } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoMuted' }
                position = { firstParticipantInList ? 'bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmptyDisabled } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.UNMUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoUnmuted' }
                position = { firstParticipantInList ? 'bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmpty } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.NONE]: null
    };

    return icons[videoMuteState];
};

type Props = {

    /**
     * Type of trigger for the participant actions.
     */
    actionsTrigger?: ActionTrigger,

    /**
     * Accessible label for the context-menu button.
     */
    ariaLabel?: string,

    /**
     * Media state for audio.
     */
    audioMediaState?: MediaState,

    /**
     * React children.
     */
    children?: Node,

    /**
     * Whether to hide the context-menu button.
     */
    hideOptions?: boolean,

    /**
     * One-based position in the list.
     */
    index?: number,

    /**
     * Extra state icons to render in the row's icon stack, for rows whose state
     * is not readable from redux (a roster of another room, say).
     */
    indicators?: Node,

    /**
     * Is this item highlighted/raised.
     */
    isHighlighted?: boolean,

    /**
     * Is Participant Moderator.
     */
    isModerator: boolean,

    /**
     * Whether the participant is the meeting's owner (drawn instead of the
     * moderator crown).
     */
    isHost?: boolean,

    /**
     * Whether the participant is hidden from the audience.
     */
    isHidden?: boolean,

    /**
     * Whether the participant is in the backstage room (rows of the backstage
     * list).
     */
    isBackstage?: boolean,

    /**
     * Whether the server is holding the participant in their room until a
     * moderator moves them.
     */
    isHeld?: boolean,

    /**
     * Whether this row represents an additional device.
     */
    isSessionRow?: boolean,

    /**
     * A precomputed capability map for rows the local store does not know
     * (server rosters, other breakout rooms). When absent, the capabilities
     * are derived from the participant in the store.
     */
    permissions?: Object,

    /**
     * The role to draw for rows the local store does not know. Defaults to the
     * participant's own role.
     */
    role?: string,


    /**
     * The name of the participant. Used for showing lobby names.
     */
    displayName?: string;

    /**
     * The name of the participant. Used for showing lobby names.
     */
    name?: string,

    /**
     * Opens this participant's context menu.
     */
    onContextMenu?: Function,

    /**
     * Callback for when the mouse leaves this component.
     */
    onLeave?: Function,

    /**
     * Expands or collapses the participant's other devices.
     */
    onToggleSessions?: Function,

    /**
     * Participant reference.
     */
    participant: Object,

    /**
     * The list used to identify its first and last rows.
     */
    participantList?: Array<Object>,

    /**
     * Whether this is the raised-hand list.
     */
    raiseHandP?: boolean,

    /**
     * Number of devices belonging to this participant.
     */
    sessionCount?: number,

    /**
     * One-based device number for an additional-device row.
     */
    sessionOrdinal?: number,

    /**
     * Whether the participant's other devices are expanded.
     */
    sessionsExpanded?: boolean,

    /**
     * Media state for video.
     */
    videoMuteState?: MediaState,
};

export const ParticipantItem = ({
    children,
    isHighlighted,
    onLeave,
    actionsTrigger = ACTION_TRIGGER.HOVER,
    audioMediaState = MEDIA_STATE.NONE,
    videoMuteState = MEDIA_STATE.NONE,
    name,
    participantList,
    raiseHandP = false,
    displayName: displayNameProp,
    isModerator,
    isHost = false,
    isHidden = false,
    isBackstage = false,
    isHeld = false,
    permissions,
    role,
    participant: p,
    hideOptions = false,
    onContextMenu,
    index,
    ariaLabel,
    isSessionRow = false,
    indicators,
    onToggleSessions,
    sessionCount = 0,
    sessionOrdinal = 0,
    sessionsExpanded = false
}: Props) => {
    const ParticipantActions = Actions[actionsTrigger] || Actions[ACTION_TRIGGER.HOVER];
    const _connectionIndicatorDisabled = useSelector(state => state['features/base/config']?.CONNECTION_INDICATOR_DISABLED);
    const firstParticipantInList = index === 1;
    const lastParticipantInList = index === (raiseHandP ? (participantList || []).filter(participant => participant?.raisedHand === true).length : (participantList || []).length);
    const isPanelist = isParticipantPanelist(p);
    const isPresenter = isParticipantPresenter(p);

    // The role the badges spell out. Rows fed from redux carry it on the
    // participant; roster rows hand it in.
    let effectiveRole = role;

    if (!effectiveRole) {
        if (isModerator) {
            effectiveRole = PARTICIPANT_ROLE.MODERATOR;
        } else if (isPanelist) {
            effectiveRole = PARTICIPANT_ROLE.PANELIST;
        } else if (isPresenter) {
            effectiveRole = PARTICIPANT_ROLE.PRESENTER;
        } else {
            effectiveRole = p?.role;
        }
    }
    const hasRoleBadge = isHost || isModerator || isPanelist || isPresenter
        || effectiveRole === PARTICIPANT_ROLE.MODERATOR || effectiveRole === PARTICIPANT_ROLE.PANELIST
        || effectiveRole === PARTICIPANT_ROLE.PRESENTER;
    const activeIndicators
        = (hasRoleBadge ? 1 : 0) + (isHidden ? 1 : 0) + (isBackstage ? 1 : 0) + (isHeld ? 1 : 0)
            + (p?.dominantSpeaker ? 1 : 0) + (p?.raisedHand ? 1 : 0) + 1;
    const displayName
        = displayNameProp || name || useSelector(getParticipantDisplayNameWithId(p?.id));
    const { t } = useTranslation();
    const sessions = useSelector(state => getParticipantSessions(state, p?.id));

    // The person's row carries a small badge saying they are here from more than
    // one place. Device labels are deliberately not spelled out: two tabs on one
    // machine report the same label, so the text is long and tells nobody
    // anything. The count, and the moderator's per-device list, do.
    const showDeviceIndicators = useSelector(areDeviceIndicatorsEnabled);
    const otherSessionCount = showDeviceIndicators ? (sessionCount || sessions.length) - 1 : 0;
    const sessionTooltip = t('sessionPersistence.alsoOnOtherDevices', { count: otherSessionCount });
    const moderatorStyle = { gap: '7px',
        display: 'flex',
        flexDirection: 'row' };

    return (
        <ParticipantContainer
            $isHighlighted = { isHighlighted }
            $isLastParticipant = { lastParticipantInList || participantList?.length === 1 }
            $isPinned = { p?.pinned }
            className = { isSessionRow ? 'participant-session-row' : undefined }
            onMouseLeave = { onLeave }
            trigger = { actionsTrigger }>
            <Avatar
                className = 'participant-avatar'
                displayName = { displayName }
                participantId = { p?.id }
                size = { isSessionRow ? 24 : 32 } />
            <ParticipantContent>
                <ParticipantDetailsContainer width = { activeIndicators >= 3 ? 50 : activeIndicators === 2 ? 60 : activeIndicators === 1 ? 70 : '' }>
                    <ParticipantNameContainer>
                        <ParticipantName><span
                            title = { displayName }>{displayName}</span></ParticipantName>
                    </ParticipantNameContainer>
                    {isSessionRow && <span className = 'participant-session-ordinal'>
                        {t('sessionPersistence.deviceNumber', { number: sessionOrdinal })}
                    </span>}
                    {!isSessionRow && otherSessionCount > 0 && (onToggleSessions
                        ? <button
                            aria-expanded = { sessionsExpanded }
                            className = 'participant-session-toggle'
                            onClick = { onToggleSessions }
                            title = { t('sessionPersistence.showDevices', { count: sessionCount }) }
                            type = 'button'>
                            <Icon
                                size = { 13 }
                                src = { IconUserGroup } />
                            <span>{sessionCount}</span>
                            <Icon
                                size = { 10 }
                                src = { sessionsExpanded ? IconArrowDownSmall : IconArrowRight } />
                        </button>
                        : <span
                            className = 'participant-session-badge'
                            title = { sessionTooltip }>
                            <Icon
                                size = { 13 }
                                src = { IconUserGroup } />
                            <span>{sessionCount}</span>
                        </span>)}

                </ParticipantDetailsContainer>
                <div className = 'participant-icon-stack'>
                    <div style = { moderatorStyle }>
                        {!_connectionIndicatorDisabled && p?.connectionStatus && (
                            <ConnectionIndicator
                                StatusIcon = { IconNetwork }
                                _connectionStatus = { p?.connectionStatus }
                                alwaysVisible = { true }
                                enableStatsDisplay = { true }
                                firstParticipantInList = { firstParticipantInList }
                                iconSize = { 16 }
                                isLocalVideo = { p?.isLocal }
                                largeVideo = { false }
                                pLocal = { p?.local }
                                participantId = { p?.id }
                                participantPane = { true }
                                statsPopoverPosition = { 'bottom-left' }
                                tooltipKey = { 'connection indicator' }
                                tooltipPosition = { 'bottom-left' } />
                        )}
                        {p?.dominantSpeaker && (
                            <Tooltip
                                className = 'small'
                                content = { 'videothumbnail.dominentSpeaker' }
                                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>

                                <div className = 'moderator-icon-participants participantpane-item-icons'>
                                    <Icon src = { IconDominantSpeaker } />
                                </div>
                            </Tooltip>
                        )}
                        <RoleIndicators
                            firstInList = { firstParticipantInList }
                            isBackstage = { isBackstage }
                            isHeld = { isHeld }
                            isHidden = { isHidden }
                            isHost = { isHost }
                            role = { effectiveRole } />
                    </div>

                    {p?.raisedHand && <Tooltip
                        className = 'small'
                        content = { 'voicecommand.hints.raiseHand' }
                        position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                        <div className = 'moderator-icon-participants participantpane-item-icons'><Icon
                            size = { 16 }
                            src = { IconRaisedHandHollow } /></div></Tooltip>}
                    {indicators}
                    {!hideOptions && <ParticipantPermissionsIndicator
                        firstInList = { firstParticipantInList }
                        participant = { p }
                        permissions = { permissions } />}
                    {getVideoStateIcon(firstParticipantInList, videoMuteState)}
                    {getAudioStateIcon(firstParticipantInList, audioMediaState)}
                    {!p?.local && !hideOptions && <ParticipantActionEllipsis
                        accessibilityLabel = { ariaLabel }
                        className = { 'participantActionEllipsis' }
                        onClick = { onContextMenu } />}
                </div>
                {!p?.local && <ParticipantActions children = { children } />}
            </ParticipantContent>
        </ParticipantContainer>
    );
};
